import type { BranchRow } from "@/modules/branches/domain/types";
import { Card } from "@/components/ui/card";

import { MapPinIcon } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import {
  Map,
  MapControls,
  MapMarker,
  MarkerContent,
  MarkerPopup,
  type MapRef,
} from "@/shared/ui/map";
import { BranchMapCircleLayers } from "./BranchMapCircleLayers";
import { MapClickHandler } from "./MapClickHandler";
import {
  branchHasCoordinates,
  computeMapBounds,
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
} from "./map-geo-utils";

export type BranchMapPoint = Pick<
  BranchRow,
  "id" | "code" | "name" | "lat" | "lng" | "serviceRadiusM" | "isActive"
>;

type BranchMapProps = {
  branches: BranchMapPoint[];
  selectedBranchId?: string | null;
  onSelectBranch?: (branch: BranchMapPoint) => void;
  className?: string;
  /** Show service radius circles */
  showRadius?: boolean;
  /** Click map background */
  onMapClick?: (lng: number, lat: number) => void;
  /** Single draggable marker mode (form picker) */
  pickerMode?: {
    lng: number;
    lat: number;
    radiusM?: number;
    onChange: (lng: number, lat: number) => void;
  };
};

function BranchMarkerPin({ active }: { active?: boolean }) {
  return (
    <div
      className={`flex h-9 w-9 items-center justify-center rounded-full border-2 shadow-md ${
        active
          ? "border-primary bg-primary text-primary-foreground"
          : "border-background bg-foreground text-background"
      }`}
    >
      <MapPinIcon className="h-4 w-4" />
    </div>
  );
}

export function BranchMap({
  branches,
  selectedBranchId,
  onSelectBranch,
  className,
  showRadius = true,
  onMapClick,
  pickerMode,
}: BranchMapProps) {
  const mapRef = useRef<MapRef | null>(null);

  const mappable = useMemo(
    () => branches.filter((b) => branchHasCoordinates(b.lat, b.lng)),
    [branches],
  );

  const circles = useMemo(() => {
    if (!showRadius) return [];
    if (pickerMode) {
      const r = pickerMode.radiusM ?? 0;
      if (r <= 0) return [];
      return [
        {
          id: "picker",
          lng: pickerMode.lng,
          lat: pickerMode.lat,
          radiusM: r,
          selected: true,
        },
      ];
    }
    return mappable.map((b) => ({
      id: b.id,
      lng: b.lng!,
      lat: b.lat!,
      radiusM: b.serviceRadiusM ?? 0,
      selected: b.id === selectedBranchId,
    }));
  }, [showRadius, pickerMode, mappable, selectedBranchId]);

  const initialCenter = useMemo((): [number, number] => {
    if (pickerMode) return [pickerMode.lng, pickerMode.lat];
    const selected = mappable.find((b) => b.id === selectedBranchId);
    if (selected) return [selected.lng!, selected.lat!];
    if (mappable.length > 0) return [mappable[0]!.lng!, mappable[0]!.lat!];
    return DEFAULT_MAP_CENTER;
  }, [pickerMode, mappable, selectedBranchId]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (pickerMode) {
      map.easeTo({ center: [pickerMode.lng, pickerMode.lat], zoom: 14 });
      return;
    }

    const selected = mappable.find((b) => b.id === selectedBranchId);
    if (selected) {
      map.easeTo({ center: [selected.lng!, selected.lat!], zoom: 13 });
      return;
    }

    const bounds = computeMapBounds(
      mappable.map((b) => ({ lng: b.lng!, lat: b.lat! })),
    );
    if (bounds) {
      map.fitBounds(bounds, { padding: 48, maxZoom: 12 });
    }
  }, [pickerMode, mappable, selectedBranchId]);

  return (
    <Card
      className={`relative h-full min-h-[280px] overflow-hidden p-0 ${className ?? ""}`}
    >
      <Map
        ref={mapRef}
        center={initialCenter}
        zoom={pickerMode ? 14 : DEFAULT_MAP_ZOOM}
        className="h-full min-h-[280px] w-full"
      >
        <MapControls
          position="bottom-right"
          showLocate={!!pickerMode}
          showZoom
          onLocate={
            pickerMode
              ? (coords) =>
                  pickerMode.onChange(coords.longitude, coords.latitude)
              : undefined
          }
        />

        {onMapClick || pickerMode ? (
          <MapClickHandler
            onClick={(lng, lat) => {
              if (pickerMode) {
                pickerMode.onChange(lng, lat);
              } else {
                onMapClick?.(lng, lat);
              }
            }}
          />
        ) : null}

        {showRadius && circles.length > 0 ? (
          <BranchMapCircleLayers circles={circles} />
        ) : null}

        {pickerMode ? (
          <MapMarker
            longitude={pickerMode.lng}
            latitude={pickerMode.lat}
            draggable
            onDragEnd={({ lng, lat }) => pickerMode.onChange(lng, lat)}
          >
            <MarkerContent>
              <BranchMarkerPin active />
            </MarkerContent>
          </MapMarker>
        ) : (
          mappable.map((branch) => (
            <MapMarker
              key={branch.id}
              longitude={branch.lng!}
              latitude={branch.lat!}
              onClick={() => onSelectBranch?.(branch)}
            >
              <MarkerContent>
                <BranchMarkerPin active={branch.id === selectedBranchId} />
              </MarkerContent>
              <MarkerPopup closeButton>
                <div className="space-y-1 p-1">
                  <p className="font-semibold text-sm">{branch.name}</p>
                  <p className="text-muted-foreground text-xs">{branch.code}</p>
                  {branch.serviceRadiusM != null ? (
                    <p className="text-muted-foreground text-xs">
                      ລັດສະໝີ: {branch.serviceRadiusM.toLocaleString()} ມ.
                    </p>
                  ) : null}
                </div>
              </MarkerPopup>
            </MapMarker>
          ))
        )}
      </Map>
    </Card>
  );
}
